/**
 * Copy of the coded Reset Metabolizmu pages (`/oferta/hollywood-reset/*`) as builder props.
 * Shared by the templates and the defaults of a newly added section.
 *
 * Tokens on top of `FUNNEL_TEXT_TOKENS`, filled by the Reset sections:
 * `{{startGrupyDzien}}` = `pon. 19.10`, `{{ratyOpisowo}}` = `2 raty po 217 zł`,
 * `{{kwota}}` = amount charged today (pay button), `{{rata}}` = next instalment (checkout FAQ),
 * `{{data}}` = date of the first scheduled payment (thank-you page).
 */

/** The double guarantee: a fixed promise, never derived from the funnel price. */
const GUARANTEE_BONUS = "794 zł";

export const resetLandingEffectsProps = { stepSlug: "landing", facebookPixel: true };

export const resetHeaderProps = { ctaLabel: "Zapisuję się", hidden: true };

export const resetHeroProps = {
  badge: "🔥 Grupa startuje {{startGrupyDzien}}",
  showSeats: true,
  title: "<strong>Schudnij 3 kg</strong> w <mark>6 tygodni</mark>.<br />Albo oddamy Ci pieniądze.",
  subtitle:
    "Reset Metabolizmu 40+ dla kobiet, którym diety przestały działać. Dietetyk 1:1, dieta w 25 minut, trening 90-60-90 w domu.",
  bullets: [
    "−3 kg albo −5 cm w talii w 6 tygodni",
    "Bez głodówek, bez liczenia kalorii, bez siłowni",
    "Schudniesz? Podwoimy Twoją inwestycję",
  ],
  ctaLabel: "Chcę schudnąć 3 kg",
  ctaNote: "{{cena}} · podwójna gwarancja",
  trust: [
    { icon: "🛡️", boldText: "Podwójna", rest: "gwarancja" },
    { icon: "👯", boldText: "18 436", rest: "kobiet już to zrobiło" },
    { icon: "⭐", boldText: "5.0", rest: "z 256 opinii" },
  ],
};

export const resetPainProps = {
  heading: "Czy to brzmi <strong>znajomo?</strong>",
  intro:
    "Masz 40, 45, 50 lat. Robisz to samo co 10 lat temu, a waga stoi albo rośnie. Unikasz zdjęć? W łazience nie patrzysz w lustro? Spodnie z zeszłego roku już się nie dopinają?",
  cards: [
    {
      emoji: "😔",
      title: "Jesz mniej niż kiedyś, a waga i tak rośnie?",
      body: "Po 40-tce ciało spala mniej kalorii, nawet gdy jesz dokładnie to samo. To nie brak silnej woli.",
    },
    {
      emoji: "🪞",
      title: "Nie poznajesz siebie w lustrze?",
      body: "Talia znika, brzuch rośnie, ramiona się zmieniają. Tłuszcz idzie tam, gdzie go nigdy nie było.",
    },
    {
      emoji: "😴",
      title: "Budzisz się zmęczona, a wieczorem nie możesz zasnąć?",
      body: "Hormony po 40-tce rozregulowują sen. A bez snu nie da się schudnąć.",
    },
  ],
  calloutTitle: "To <strong>nie</strong> jest Twoja wina.",
  calloutParagraphs: [
    "Twoje ciało po 40-tce gra według nowych zasad. A Ty walczysz starymi metodami: głodówką i godzinami cardio.",
    "Nic dziwnego, że nie działa.",
  ],
};

export const resetRulesProps = {
  eyebrow: "Nauka, nie opinia",
  heading: "Po 40-tce Twoje ciało zmienia <strong>3 zasady gry</strong>",
  items: [
    {
      num: "01",
      emoji: "💪",
      title: "Twoje mięśnie znikają",
      sub: "1-2% rocznie po 40-tce",
      body: "Każdego roku tracisz trochę mięśni. A mięśnie to Twój piec do spalania. Mniejszy piec = tyjesz z tego samego jedzenia.",
    },
    {
      num: "02",
      emoji: "🧬",
      title: "Hormony grają w inną grę",
      sub: "Zaczyna się około 40-tki",
      body: "Estrogen spada, kortyzol rośnie. Ciało zaczyna chomikować tłuszcz, zamiast go spalać. Najbardziej na brzuchu.",
    },
    {
      num: "03",
      emoji: "🎯",
      title: "Tłuszcz idzie prosto na brzuch",
      sub: "Stres + głodówka = oporny brzuch",
      body: "Stres i głodówki podbijają kortyzol. A kortyzol każe ciału trzymać tłuszcz na brzuchu. Dlatego kolejna dieta nie rusza brzucha.",
    },
  ],
  calloutTitle: "Dlatego głodówki i godziny cardio przestały działać.",
  calloutText: "Po 40-tce potrzebujesz <strong>resetu</strong>, a nie kolejnej diety.",
};

export const resetSolutionProps = {
  eyebrow: "Nowy mechanizm",
  heading: "Reset Metabolizmu <strong>zamiast</strong> kolejnej diety",
  intro: "6-tygodniowy protokół, który robi 3 rzeczy, których nie zrobi żadna dieta:",
  cards: [
    {
      num: "01",
      title: "Wyłącza głód i napady na słodkie",
      body: "Jesz do syta, 3 razy dziennie. Hormony się uspokajają, a ochota na słodkie znika.",
    },
    {
      num: "02",
      title: "Odbudowuje mięśnie metodą 90-60-90",
      body: "40 minut w domu co drugi dzień. Mięśnie wracają, a z nimi szybszy metabolizm.",
    },
    {
      num: "03",
      title: "Przestawia metabolizm na spalanie 24/7",
      body: "Ciało spala tłuszcz cały dzień, także w nocy, a nie tylko na treningu.",
    },
  ],
  closingText: "Waga spada. Talia się zmniejsza. Energia wraca.",
  closingBold: "Bez głodu. Bez siłowni. Bez liczenia kalorii.",
};

export const resetStackProps = {
  eyebrow: "Co dostajesz",
  heading: "Co dostajesz w <strong>Resecie Metabolizmu 40+</strong>",
  items: [
    {
      icon: "🎯",
      title: "Hormonalny plan resetujący metabolizm",
      body: "Plan jedzenia ułożony dla Ciebie przez dietetyka. Odblokowuje metabolizm i zatrzymuje odkładanie tłuszczu na brzuchu.",
      priceValue: "400 zł",
      priceSub: "",
    },
    {
      icon: "👩🏻‍⚕️",
      title: "6 konsultacji 1-na-1",
      body: "Co tydzień rozmowa z dietetykiem: postępy, zmiany w planie, rozwiązywanie problemów.",
      priceValue: "900 zł",
      priceSub: "(6 × 150 zł)",
    },
    {
      icon: "💪",
      title: "Trening 90-60-90 w domu",
      body: "18 treningów po 40 minut metodą 90-60-90. Modeluje talię, brzuch i pośladki. Bez siłowni, minimalny sprzęt.",
      priceValue: "400 zł",
      priceSub: "",
    },
    {
      icon: "🍳",
      title: "Dieta w 25 minut",
      body: "Proste przepisy, które zje cała rodzina. Gotujesz raz, nie dwa obiady.",
      priceValue: "200 zł",
      priceSub: "",
    },
    {
      icon: "⚡",
      title: "Protokół na szybki start",
      body: "Plan na pierwsze 7 dni, żeby od razu zobaczyć różnicę na wadze.",
      priceValue: "150 zł",
      priceSub: "",
    },
    {
      icon: "💬",
      title: "Dietetyk na czacie 8:00-18:00",
      body: "Piszesz w aplikacji, odpowiedź w ciągu 3 godzin. Człowiek, nie bot.",
      priceValue: "300 zł",
      priceSub: "",
    },
    {
      icon: "👯",
      title: "Społeczność 18 436 kobiet",
      body: "Prywatna grupa. Wsparcie i motywacja od kobiet, które przeszły to samo.",
      priceValue: "w cenie",
      priceSub: "",
    },
  ],
  totalLabel: "Łączna wartość",
  totalValue: "2 350 zł",
  investmentLabel: "Twoja inwestycja",
  price: "{{cena}}",
  priceAlt: "lub {{raty}}",
};

export const resetBonusesProps = {
  heading: "+ <strong>3 bonusy</strong> dla zapisanych do grupy {{startGrupy}}",
  intro: "Specjalne zasoby, których nie znajdziesz nigdzie indziej:",
  items: [
    {
      tag: "Bonus 01",
      emoji: "🎯",
      title: "Płaski brzuch 24/7 - protokół",
      body: "Specjalny protokół dla kobiet, u których „oporny brzuch” nie reaguje na żadną dietę. Co jeść, jak ćwiczyć, jak zarządzać kortyzolem - żeby spalanie tłuszczu z brzucha trwało także w nocy.",
      priceValue: "297 zł",
    },
    {
      tag: "Bonus 02",
      emoji: "🌸",
      title: "Masterclass Programu Hollywood",
      body: "Dostęp do 2 h szkoleń tygodniowo - żeby dowiedzieć się, jak wyglądać lepiej niż 10 lat temu (i utrzymać to na zawsze).",
      priceValue: "397 zł",
    },
    {
      tag: "Bonus 03",
      emoji: "🍷",
      title: "Protokół na piątkowe zachcianki",
      body: "Jak nie rezygnować z wina i słodyczy podczas odchudzania. Bo życie ma być przyjemnością, a nie wyrzeczeniem.",
      priceValue: "97 zł",
    },
  ],
};

export const resetGuaranteeProps = {
  badgeValue: "2×",
  badgeLabel: "Podwójna gwarancja",
  heading: "<strong>Podwójna gwarancja.</strong> Nie możesz przegrać.",
  intro: "Twoje jedyne zadanie: zrobić plan przez 6 tygodni.",
  checklist: [
    "Robisz minimum 80% treningów 90-60-90 (15 z 18)",
    "Jesz według planu",
    "Co tydzień wysyłasz dietetykowi wagę i obwody (6 raportów w aplikacji)",
  ],
  dividerLabel: "Na koniec Resetu masz dwie opcje. Obie wygrywasz:",
  options: [
    {
      icon: "✅",
      text: `<strong>Schudłaś 3 kg albo 5 cm w talii?</strong> Podwajamy Twoją inwestycję: dostajesz <em>${GUARANTEE_BONUS}</em> na dalszą metamorfozę w Programie Hollywood.`,
    },
    {
      icon: "🛡️",
      text: "<strong>Nie schudłaś?</strong> Oddajemy <em>100% pieniędzy</em>. Bez pytań. Albo prowadzimy Cię za darmo, aż schudniesz.",
    },
  ],
  closing: "Jedyny sposób, żeby stracić, to nic nie robić.",
  footnote: `${GUARANTEE_BONUS} wykorzystasz przy zapisie na pełny Program Hollywood (pakiet od 3 miesięcy) w ciągu 7 dni od końca Resetu. Szczegóły w regulaminie.`,
};

export const resetTestimonialProps = {
  eyebrow: "Rezultaty po 6 tygodniach",
  heading: "Co kobiety osiągają w <strong>6 tygodni?</strong>",
  videoId: "jh2apd3imj",
  badgeTop: "−4 kg",
  badgeSub: "w 6 tygodni",
  avatar: "👩🏻‍🦰",
  who: "Marta S., 47 lat",
  meta: "Hashimoto · 2 lata bez efektów",
  quote:
    "Przez 2 lata z Hashimoto nie schudłam ani kilograma. Po 6 tygodniach miałam −4 kg, a po miesiącu mąż mówił „już wystarczy”. W końcu lubię swoje ciało!",
};

export const resetLongTermProps = {
  eyebrow: "Co dalej po 6 tygodniach?",
  heading: "Reset Hollywood to <strong>pierwszy etap</strong>",
  intro: "Tak wygląda droga klientek, które kontynuowały swoją metamorfozę w pełnym Programie Hollywood:",
  transformationIds: ["45", "43", "34", "46", "31", "47"],
  closing: `Po Resecie przechodzisz do pełnego Programu z ${GUARANTEE_BONUS} z podwójnej gwarancji.`,
};

export const resetTimelineProps = {
  eyebrow: "Jak to działa",
  heading: "6 tygodni, <strong>szybkie efekty</strong>",
  weeks: [
    {
      meta: "⚡ Tydzień 1 · Protokół na szybki sukces",
      title: "Pierwsze 2-3 kg + analiza startowa",
      body: "Gubisz pierwsze 2-3 kg dzięki specjalnemu protokołowi startowemu. Pierwsza konsultacja z dietetykiem - analizuje Twoje zdrowie, hormony, preferencje.",
    },
    {
      meta: "🎯 Tydzień 2 · Personalizacja",
      title: "Plan dopasowany do Twojego ciała",
      body: "Po zebraniu danych z 1. tygodnia, dietetyk modyfikuje plan. Wchodzisz w rytm. Dieta i treningi stają się nawykiem.",
    },
    {
      meta: "🔥 Tydzień 3 · Tryb spalania",
      title: "Twoje ciało spala tłuszcz 24/7",
      body: "Wchodzisz w stan, w którym ciało spala tłuszcz całą dobę. Energia rośnie. Ubrania zaczynają luźniej leżeć.",
    },
    {
      meta: "✨ Tydzień 4 · Przełom",
      title: "Pierwsze komplementy",
      body: "Pierwsze komplementy od bliskich. Patrzysz w lustro inaczej. Mierzysz obwody - różnica jest mierzalna.",
    },
    {
      meta: "💪 Tydzień 5 · Stabilizacja i mięśnie",
      title: "Sylwetka się modeluje, nie tylko zmniejsza",
      body: "Mięśnie zaczynają wracać. Sylwetka się modeluje. Spalanie staje się „automatyczne”.",
    },
    {
      meta: "🏁 Tydzień 6 · Wynik finalny",
      title: "Pomiar końcowy + decyzja",
      body: `Pomiar końcowy. Odbierasz ${GUARANTEE_BONUS} na dalszą metamorfozę albo zwrot pieniędzy. Decydujesz, co dalej.`,
    },
  ],
};

export const resetOfferProps = {
  href: "",
  deadlineLabel: "Zapisy do grupy {{startGrupy}} zamykamy w niedzielę o 23:59",
  countdownLabel: "Zapisy zamykamy za:",
  heading:
    "Możesz spróbować jeszcze raz po swojemu. Albo dołączyć do <strong>18 436 kobiet</strong>, które przestały próbować.",
  intro:
    "Możesz odpuścić i za rok być w tym samym miejscu. Albo dołączyć teraz, niczym nie ryzykując, i za 6 tygodni mieć:",
  benefits: [
    "<strong>−3 kg</strong> albo <strong>−5 cm</strong> w talii",
    "Energię <strong>jak 10 lat temu</strong>",
    "Wiedzę, jak <strong>utrzymać to na zawsze</strong>",
    `<strong>${GUARANTEE_BONUS}</strong> na dalszą metamorfozę`,
  ],
  cardEyebrow: "Program Hollywood",
  cardTitle: "Reset Metabolizmu 40+",
  valueLabel: "Wartość pakietu z bonusami",
  valueStrike: "3 141 zł",
  investmentLabel: "Twoja inwestycja",
  price: "{{cena}}",
  priceAlt: "lub <strong>{{raty}}</strong>",
  startText: "Start grupy: <strong>{{startGrupyDzien}}</strong>",
  showSeats: true,
  ctaLabel: "Zapisuję się na Reset · {{cena}}",
  ctaNote: "🛡️ Podwójna gwarancja albo zwrot 100%",
  badges: ["🛡️ Podwójna gwarancja", "🎁 3 bonusy dla grupy {{startGrupy}}"],
  closing:
    "Startujesz w <strong>poniedziałek {{startGrupy}}</strong>. Dietetyk dzwoni do Ciebie przed startem. Każdy tydzień zwlekania to tydzień, którego nie odzyskasz.",
};

export const resetFaqProps = {
  eyebrow: "Najczęstsze pytania",
  heading: "Zanim się <strong>zapiszesz</strong>",
  items: [
    {
      q: "Mam 35 lat. Czy to dla mnie?",
      a: "Tak. Metabolizm zaczyna zwalniać już po 35-tce. Reset odwraca to, zanim zrobi się trudniej. Dietetyk dopasuje plan do Twojego wieku i trybu życia.",
    },
    {
      q: "Mam 52 lata i jestem już po menopauzie - czy to dla mnie?",
      a: "Tak. Po menopauzie mięśnie i dobrze ułożone jedzenie są jeszcze ważniejsze. Plan i trening dopasujemy do Ciebie. Wiele naszych podopiecznych jest po 50-tce.",
    },
    {
      q: "Czy to działa przy Hashimoto / niedoczynności tarczycy?",
      a: "Tak - ponad 30% naszych podopiecznych to kobiety z Hashimoto. Plan jest dopasowywany przez dietetyka indywidualnie z uwzględnieniem TSH, fT3, fT4 oraz przyjmowanych leków. Marta z naszego testimoniala miała Hashimoto i 2 lata bez efektów - w 6 tygodni zgubiła 4 kg.",
    },
    {
      q: "Czy muszę chodzić na siłownię?",
      a: "Nie. Trening 90-60-90 robisz w domu, 40 minut co drugi dzień, z minimalnym sprzętem. Każde ćwiczenie jest pokazane na filmie.",
    },
    {
      q: "Ile czasu to zajmuje dziennie?",
      a: "Około 25 minut gotowania i 40 minut treningu co drugi dzień.",
    },
    {
      q: "Co jeśli nie schudnę?",
      a: "Jeśli zrobisz plan i nie schudniesz 3 kg albo 5 cm w talii, oddajemy 100% pieniędzy albo prowadzimy Cię za darmo, aż schudniesz.",
    },
    {
      q: `Jak odebrać ${GUARANTEE_BONUS}?`,
      a: `W 6. tygodniu robisz z dietetykiem pomiar końcowy. Jeśli masz −3 kg albo −5 cm w talii, dostajesz ${GUARANTEE_BONUS} na koncie w aplikacji. Wykorzystasz je przy zapisie na pełny Program Hollywood.`,
    },
    {
      q: "Kiedy zaczyna się najbliższa grupa?",
      a: "W poniedziałek {{startGrupy}}. Zapisy zamykamy w niedzielę o 23:59 albo wcześniej, gdy skończą się miejsca (30 na grupę).",
    },
    {
      q: "Mogę płacić w ratach?",
      a: "Tak: {{ratyOpisowo}}. Pierwsza przy zapisie, druga po 30 dniach. Nic więcej nie pobieramy.",
    },
    {
      q: "Czy coś mi się samo przedłuży?",
      a: "Nie. Płacisz za 6 tygodni. Po Resecie sama decydujesz, czy zostajesz w Programie Hollywood.",
    },
  ],
};

export const resetStickyCtaProps = {
  productLabel: "Reset Metabolizmu 40+",
  note: "🛡️ Podwójna gwarancja",
  ctaLabel: "Zapisuję się",
};

export const resetCheckoutProps = {
  seoTitle: "Reset Metabolizmu 40+ - płatność | Program Hollywood",
  heroTitle: "Zapisujesz się na <strong>Reset Metabolizmu 40+</strong>",
  heroSubtitle: "6 tygodni z własnym dietetykiem. Start grupy: <strong>{{startGrupyDzien}}</strong>.",
  showSeats: true,
  productIcon: "🎯",
  productName: "Reset Metabolizmu Hollywood 40+",
  productTagline: "Dietetyk 1:1, dieta w 25 min, trening 90-60-90 w domu, gwarantowane efekty",
  productItems: [
    "Własny dietetyk na czacie, odpowiedź w ciągu 3 h (8:00-18:00)",
    "6 rozmów telefonicznych, co tydzień",
    "Plan jedzenia pod Ciebie, ok. 25 min gotowania dziennie",
    "18 treningów 90-60-90 w domu, 40 min, bez siłowni",
    "Plan na pierwsze 7 dni, żeby szybko poczuć zmianę",
    "Aplikacja Hollywood: jadłospis, lista zakupów, treningi",
    "Zamknięta grupa z innymi kobietami",
  ],
  guaranteeText: `🛡️ <strong>Podwójna gwarancja.</strong> Schudniesz 3 kg albo 5 cm w talii? Podwajamy Twoją inwestycję: <strong>${GUARANTEE_BONUS}</strong> na dalszą metamorfozę. Nie schudniesz? Zwrot 100%.`,
  guaranteeDetails:
    "Warunek: robisz min. 80% treningów 90-60-90, jesz według planu i co tydzień wysyłasz dietetykowi wagę i obwody. Bez efektu wybierasz: zwrot 100% pieniędzy albo dalsze prowadzenie za darmo, aż schudniesz.",
  phoneHelpText: "Dietetyk zadzwoni przed startem. Nie dzwonimy z reklamami.",
  countdownLabel: "Zapisy do grupy {{startGrupy}} zamykamy za:",
  payButtonLabel: "Zapłać {{kwota}} i zresetuj metabolizm",
  payButtonNote: "Gwarancja efektu albo zwrot 100%",
  afterPaymentSteps: [
    { when: "Za 2 minuty", what: "E-mail z dostępem do aplikacji Hollywood." },
    { when: "Dziś, 5 minut", what: "Krótka ankieta, żeby dietetyk poznał Twój dzień." },
    { when: "Do 48 godzin", what: "Dietetyk dzwoni i układa Twój plan. Startujesz z grupą {{startGrupy}}." },
  ],
  faq: [
    {
      q: "Czy coś mi się samo przedłuży?",
      a: "Nie. Płacisz za 6 tygodni. Po Resecie sama decydujesz, czy zostajesz.",
      aInstallments:
        "Nie. Płacisz za 6 tygodni. Przy ratach pobieramy tylko drugą ratę {{rata}}. Po Resecie sama decydujesz, czy zostajesz.",
    },
    {
      q: "Mam mało czasu. Dam radę?",
      a: "Gotowanie to ok. 25 minut dziennie, trening 40 minut co drugi dzień, w domu. Dietetyk ułoży plan pod Twój prawdziwy dzień.",
      aInstallments: "",
    },
    {
      q: "Mam Hashimoto albo insulinooporność.",
      a: "Dietetyk dopasuje plan do Ciebie. Jeśli leczysz się u lekarza, powiedz dietetykowi o jego zaleceniach.",
      aInstallments: "",
    },
  ],
  stickyLabel: "Reset Metabolizmu",
};

export const resetThankYouProps = {
  eyebrow: "Dziękujemy za zakup",
  title: "Pomyślnie zakupiono produkt - sprawdź swój e-mail",
  description:
    "Na Twoją skrzynkę e-mail wysłaliśmy informacje na temat dokończenia procesu zakupu. Jeżeli nie widzisz wiadomości w głównej skrzynce, sprawdź folder SPAM lub OFERTY.",
  renewalTitle: "Pomyślnie odnowiono dostęp do programu",
  renewalDescription:
    "Dziękujemy za odnowienie dostępu do programu. Zaktualizowane informacje o Twoim dostępie znajdziesz w aplikacji.",
  orderNumberLabel: "Numer zamówienia",
  billingLabel: "Dane kupującego",
  paymentMethodLabel: "Metoda płatności",
  paidAmountLabel: "Zapłacona kwota",
  firstPaymentLabel: "Kwota pierwszej płatności ({{data}})",
  confetti: true,
};
